import React, { useState, useEffect } from 'react';
import { authHeaders } from '../../lib/apiAuth';
import { T, SPARKY_SERVER_URL } from '../shared/constants';
import {
  Badge, DifficultyBadge, Card, ProgressBar, MultilineText,
} from '../shared/ui';
import { QuestionBody, ReportQuestionButton } from './questionParts';
import { StoredFeedback, PASSAGE_LAYOUT_CSS } from './ItemChrome';
import { answerText, feedbackFor } from './naplanItems';

/**
 * MCQRendererProps
 *
 * question        — QB question object
 *                   { id, q, options, answer, topic, difficulty, hint?, explanation?, criteria?, year? }
 * studentAnswer   — { questionId, questionType:'mcq', rawAnswer:number, isCorrect:boolean, submittedAt:string } | null
 *                   null  → no answer yet (options clickable)
 *                   non-null → answered (feedback visible, options locked)
 * state           — { mode:'practice'|'exam'|'topic', current:number, total:number }
 * showExplanation — boolean (parent controls; typically: studentAnswer!==null && mode!=='exam')
 * onSubmit        — (answer) => void  fires immediately on option click
 * onAskLumi?      — () => void
 * onExplainVideo? — (question) => void
 */

function DiagnosticPanel({ question, chosenIdx, yearId, serverUrl }) {
  const [diag, setDiag]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    if (!fetched) { setFetched(true); fetchDiagnosis(); }
  }, []);

  async function fetchDiagnosis() {
    setLoading(true);
    const chosen  = question.options[chosenIdx];
    const correct = question.options[question.answer];
    const system = `You are Lumi, an expert NAPLAN diagnostic tutor. A student got a question wrong.
Analyse WHY they made that mistake and give targeted help.
Return ONLY valid JSON — no markdown, no preamble.

{
  "misconception": "The specific misconception or error pattern (1 sentence, e.g. 'Confused numerator and denominator')",
  "understood": "What they DO understand correctly (1 sentence, encouraging)",
  "micro_lesson": "A targeted 2-sentence explanation addressing the exact misconception",
  "practice_tip": "One specific thing to do next time (e.g. 'Always identify the total first')",
  "error_type": "conceptual|procedural|careless|vocabulary"
}`;
    const user = `Question: "${question.q}"
Topic: ${question.topic}
Year level: ${yearId?.replace('y','')}
Correct answer: "${correct}"
Student chose: "${chosen}"
Explanation: "${question.explanation}"

Diagnose why the student chose "${chosen}" instead of "${correct}".`;
    try {
      const res  = await fetch(`${serverUrl}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          model: 'claude-haiku-5-5',
          max_tokens: 400,
          system,
          messages: [{ role: 'user', content: user }],
        }),
      });
      const data  = await res.json();
      const text  = data.content?.[0]?.text || '';
      const clean = text.replace(/```json|```/g, '').trim();
      setDiag(JSON.parse(clean));
    } catch(e) {
      setDiag({
        misconception: 'A common mix-up in this topic area.',
        understood: 'You understood part of the question correctly.',
        micro_lesson: question.explanation,
        practice_tip: question.hint || 'Read each option carefully before choosing.',
        error_type: 'conceptual',
      });
    } finally {
      setLoading(false);
    }
  }

  const errorColors = {
    conceptual: { bg: '#EF444415', border: '#EF4444', label: 'Concept Gap',    icon: '🧠' },
    procedural: { bg: '#F59E0B15', border: '#F59E0B', label: 'Method Error',   icon: '📐' },
    careless:   { bg: '#3B82F615', border: '#3B82F6', label: 'Careless Error', icon: '👀' },
    vocabulary: { bg: '#8B5CF615', border: '#8B5CF6', label: 'Vocabulary Gap', icon: '📚' },
  };

  if (loading) return (
    <div style={{ padding: '12px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#FBBF24,#F59E0B)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#0A0C10', flexShrink: 0 }}>⚡</div>
      <div style={{ display: 'flex', gap: 4 }}>
        {[0,1,2].map(i => <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#F59E0B', animation: `lumi-idle ${0.6+i*0.2}s ease-in-out infinite` }}/>)}
        <span style={{ fontSize: 12, color: T.textMid, marginLeft: 4 }}>Lumi is working out what happened...</span>
      </div>
    </div>
  );

  if (!diag) return null;
  const ec = errorColors[diag.error_type] || errorColors.conceptual;

  return (
    <div style={{ marginTop: 12, animation: 'fadeUp 0.4s ease' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: ec.bg, border: `1px solid ${ec.border}44`, borderRadius: 99, padding: '3px 10px', marginBottom: 10 }}>
        <span>{ec.icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: ec.border }}>{ec.label}</span>
      </div>
      <div style={{ background: `${T.red}10`, border: `1px solid ${T.red}33`, borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
        <p style={{ fontSize: 11, color: T.red, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>What happened</p>
        <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.55 }}>{diag.misconception}</p>
      </div>
      <div style={{ background: `${T.green}10`, border: `1px solid ${T.green}33`, borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
        <p style={{ fontSize: 11, color: T.green, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>What you got right</p>
        <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.55 }}>{diag.understood}</p>
      </div>
      <div style={{ background: `${T.accent}10`, border: `1px solid ${T.accent}33`, borderRadius: 10, padding: '10px 14px', marginBottom: 8 }}>
        <p style={{ fontSize: 11, color: T.accent, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>The fix</p>
        <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.6 }}>{diag.micro_lesson}</p>
      </div>
      <div style={{ background: `${T.gold}10`, border: `1px solid ${T.gold}33`, borderRadius: 10, padding: '10px 14px' }}>
        <p style={{ fontSize: 11, color: T.gold, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Remember next time</p>
        <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.55 }}>💡 {diag.practice_tip}</p>
      </div>
    </div>
  );
}

export default function MCQRenderer({
  question,
  studentAnswer,
  state,
  showExplanation,
  onSubmit,
  onAskLumi,
  onExplainVideo,
}) {
  const { mode, current, total } = state;
  const isAnswered = studentAnswer !== null;
  const chosen     = studentAnswer?.rawAnswer ?? null;
  const isCorrect  = studentAnswer?.isCorrect ?? false;
  // Stored, item-specific feedback (`why`) replaces the AI diagnosis; the
  // /api/chat DiagnosticPanel is only the fallback for items without it.
  const hasStoredWhy = Array.isArray(question.why) || !!question.whyDefault;
  const storedWhy    = hasStoredWhy && isAnswered && !isCorrect ? feedbackFor(question, chosen) : null;

  function handleOptionClick(i) {
    if (isAnswered) return;
    onSubmit({
      questionId:   question.id,
      questionType: 'mcq',
      rawAnswer:    i,
      isCorrect:    i === question.answer,
      submittedAt:  new Date().toISOString(),
    });
  }

  return (
    <div className={question.passage ? "lumio-item has-passage" : "lumio-item"} style={{ animation: "fadeUp .3s ease", padding: "24px 32px 16px", maxWidth: 860, margin: "0 auto", width: "100%" }}>
      <style>{PASSAGE_LAYOUT_CSS}</style>
      {/* Question meta + report flag */}
      <div className="lumio-span" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        <Badge label={`Q ${current} / ${total}`} color={T.textMid} />
        <Badge label={question.topic} color={T.accent} />
        <DifficultyBadge level={question.difficulty} />
        {mode !== "exam" && question.criteria && <Badge label={question.criteria.split("—")[0].trim()} color={T.purple} />}
        <div style={{ marginLeft:"auto", flexShrink:0 }}>
          <ReportQuestionButton question={question} />
        </div>
      </div>

      {/* Progress */}
      <div className="lumio-span"><ProgressBar value={current} max={total} height={3} style={{ marginBottom: 24 }} /></div>

      {/* Passage (reading questions) */}
      {question.passage && (
        <div className="lumio-passage" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 20, fontSize: 13, color: T.textMid, lineHeight: 1.7, whiteSpace: "pre-line" }}>
          {question.passage}
        </div>
      )}

      {/* Question, options and feedback: the right-hand column beside a passage on wide screens */}
      <div>
      <Card style={{ marginBottom: 20, padding: "24px" }}>
        <QuestionBody text={question.q} />
        {mode === "practice" && !isAnswered && question.hint && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 12, color: T.textDim, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Hint</p>
            <p style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>💡 {question.hint}</p>
          </div>
        )}
      </Card>

      {/* Options */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
        {question.options.map((opt, i) => {
          let bg = T.card, border = T.border, color = T.text, icon = null;
          if (isAnswered) {
            if (i === question.answer)  { bg = T.greenLo; border = T.green; color = T.green; icon = "✓"; }
            else if (i === chosen)      { bg = T.redLo;   border = T.red;   color = T.red;   icon = "✗"; }
          }
          return (
            <button key={i} onClick={() => handleOptionClick(i)} style={{
              background: bg, border: `1.5px solid ${border}`, borderRadius: 12,
              padding: "14px 18px", textAlign: "left", cursor: isAnswered ? "default" : "pointer",
              display: "flex", alignItems: "center", gap: 12, transition: "all .2s",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
            onMouseOver={e => { if (!isAnswered) e.currentTarget.style.borderColor = T.borderHi; }}
            onMouseOut={e  => { if (!isAnswered) e.currentTarget.style.borderColor = T.border;   }}
            >
              <span style={{ width: 28, height: 28, borderRadius: 8, background: isAnswered && i === question.answer ? T.green + "33" : isAnswered && i === chosen ? T.red + "33" : T.surface, border: `1px solid ${border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color, flexShrink: 0 }}>
                {icon || ["A","B","C","D","E","F"][i]}
              </span>
              <span style={{ fontSize: 15, color, fontWeight: isAnswered && (i === question.answer || i === chosen) ? 600 : 400, lineHeight: 1.5 }}>{opt != null ? String(opt) : ""}</span>
            </button>
          );
        })}
      </div>

      {/* Explanation */}
      {showExplanation && (
        <Card style={{ marginBottom: 20, background: isCorrect ? T.greenLo : T.redLo, borderColor: isCorrect ? T.green : T.red, animation: "scaleIn .3s ease", padding: "20px" }}>
          <p style={{ fontWeight: 700, color: isCorrect ? T.green : T.red, marginBottom: 8, fontSize: 15 }}>
            {isCorrect ? "✓ Correct!" : "✗ Incorrect"}
          </p>
          <MultilineText text={question.explanation} style={{ color: T.text, fontSize: 14, lineHeight: 1.7, marginBottom: mode === "practice" ? 14 : 0 }} />
          {!isCorrect && isAnswered && storedWhy && (
            <StoredFeedback message={storedWhy} hint={question.hint} />
          )}
          {!isCorrect && isAnswered && mode === "practice" && !hasStoredWhy && (
            <DiagnosticPanel
              key={question.id}
              question={question}
              chosenIdx={chosen}
              yearId={question.year?.[0] || "y5"}
              serverUrl={SPARKY_SERVER_URL}
            />
          )}
          {mode === "practice" && (
            <div style={{ paddingTop: 12, borderTop: `1px solid ${isCorrect ? T.green + "33" : T.red + "33"}` }}>
              <p style={{ fontSize: 12, color: T.textDim, marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Assessment Criteria</p>
              <p style={{ fontSize: 13, color: T.textMid }}>{question.criteria || ""}</p>
            </div>
          )}
        </Card>
      )}

      </div>
    </div>
  );
}
